import * as React from 'react';
import { connect } from 'react-redux';
import Select from 'react-select';
import { UilMultiply } from '@iconscout/react-unicons';
import { getQuestionByQuestionId } from '../../../../../../services/question.service';
import { IApplicationState } from '../../../../../../reducers';
import {
    GROUP_QTYPES,
    PIPING_OPTION_QTYPES
} from '../../../../../../constants/question';
import {
    OptionData,
    ProjectLanguage,
    QTYPES,
    Question,
    QuestionSummary
} from '@nexxt/common/types';
import { NestedOption as Option } from '../../../../../common/reactSelect/Option';
import { flattenQuestionSummary } from '../../../../../../utils/question';
import { getDemographicDetails } from '@nexxt/common/demographics/index';
import Toggle from '../../../../../common/Toggle';
import ReactTooltip from 'react-tooltip';

interface PipingProps {
    questionSummaries: QuestionSummary[];
    question: Question;
    disabled: boolean;
    languages: ProjectLanguage[];
    countries: any;
    getQuestionById: (id: number, options: any) => Promise<Question>;
    setPipingOptions: (options: OptionData[]) => void;
    setQuestionAsQuestion: (value: Question) => void;
    onClose: () => void;
}

const PIPING_OPERATOR = [
    { label: 'SELECTED', value: false },
    { label: 'UNSELECTED', value: true }
];
const Piping: React.FC<PipingProps> = ({
    questionSummaries,
    question,
    disabled,
    languages,
    countries,
    getQuestionById,
    setPipingOptions,
    setQuestionAsQuestion,
    onClose
}: PipingProps) => {
    const [flattenedQuestionSummaries, setFlattenedQuestionSummaries] =
        React.useState([]);
    React.useEffect(() => {
        if (question.piping && question.pipingQuestions?.length) {
            init(question.pipingQuestions[0].id);
        }

        setFlattenedQuestionSummaries(
            flattenQuestionSummary(questionSummaries)
        );
    }, []);

    const init = async (id) => {
        const question = await getQuestion(id);
        const options = await getOptions(question);
        setPipingOptions(options);
    };

    const getQuestion = async (id): Promise<Question> => {
        return await getQuestionById(id, { logicList: true });
    };
    const getOptions = async (question: Question): Promise<OptionData[]> => {
        const options = [];
        if (question.isAudience && question.builtInID) {
            const referenceQuestion = getDemographicDetails(
                languages,
                question.builtInID,
                countries?.[0]?.value,
                null,
                { questionId: question.id as number }
            );
            options.push(...referenceQuestion.options);
        } else {
            options.push(...question.options);
        }
        if (question?.optionSettings?.showOther) {
            options.push(question?.optionSettings?.otherOption);
        }
        if (question.piping && question.pipingQuestions.length) {
            const questionId = question.pipingQuestions[0].id;
            const pipingQuestion = await getQuestion(questionId);
            options.push(...(await getOptions(pipingQuestion)));
        }

        return options;
    };
    const formatQuestionSummaries = (questionSummaries, level = 0) => {
        let newSummaries = [];
        newSummaries = questionSummaries
            .filter(
                (q) =>
                    (PIPING_OPTION_QTYPES.includes(q.type as QTYPES) ||
                        GROUP_QTYPES.includes(q.type as QTYPES)) &&
                    q.id !== question.id
            )
            .map((questionSummary) => {
                if (questionSummary.childrenIds.length) {
                    const options = formatQuestionSummaries(
                        questionSummary.childrenSummaries,
                        level + 1
                    );
                    return { ...questionSummary, options, level };
                }
                return { ...questionSummary, level };
            });

        return newSummaries;
    };
    const selectQuestion = async (questionSummary: QuestionSummary) => {
        const { pipingQuestions } = question;

        const pipingQuestion = await getQuestion(questionSummary.id);

        const options = await getOptions(pipingQuestion);
        /**
         * @TODO update question property
         * selectedAlignOption, selectedOptionType, optionsLayout
         * min, max, random
         * notApplicableTexts, showNotApplicableOption
         * optionSettings.showOther, optionSettings.showSpecify, optionSettings.otherTexts
         */
        setQuestionAsQuestion({
            ...question,
            selectedOptionType: pipingQuestion.selectedOptionType,
            min: pipingQuestion.min,
            max: pipingQuestion.max,
            random: pipingQuestion.random,
            pipingQuestions: [
                {
                    id: questionSummary.id,
                    reversePiping: pipingQuestions[0]?.reversePiping || false
                }
            ]
        });
        setPipingOptions(options);
    };

    const selectOperator = (opt) => {
        const { pipingQuestions } = question;
        setQuestionAsQuestion({
            ...question,
            pipingQuestions: [
                { ...pipingQuestions[0], reversePiping: opt.value }
            ]
        });
    };
    const toggleEnableSkip = () => {
        const { pipingQuestions } = question;
        setQuestionAsQuestion({
            ...question,
            pipingQuestions: [
                {
                    ...pipingQuestions[0],
                    enableSkip: !pipingQuestions?.[0]?.enableSkip || false
                }
            ]
        });
    };

    const filteredQuestionSummaries =
        formatQuestionSummaries(questionSummaries);
    const selectedOperator = PIPING_OPERATOR.find(
        (opt) => opt.value === question.pipingQuestions[0]?.reversePiping
    );
    const selectedValue = flattenedQuestionSummaries.find(
        (q) =>
            question?.pipingQuestions?.length &&
            q.id == question?.pipingQuestions[0]?.id
    );
    const enableSkip = question.pipingQuestions?.[0]?.enableSkip;
    const referenceError = !selectedValue && question?.pipingQuestions[0]?.id;
    return (
        <div className="options--container--body--piping--container">
            <div className="options--container--body--piping--wrapper">
                <span>PIPE </span> FROM{' '}
                <Select
                    className="options--container--body--piping--select"
                    classNamePrefix={`${referenceError ? 'error' : ''} piping`}
                    options={filteredQuestionSummaries}
                    components={{ Option }}
                    onChange={selectQuestion}
                    value={selectedValue}
                    isDisabled={disabled}
                />
                if
                <Select
                    // menuIsOpen
                    className="options--container--body--piping--select"
                    classNamePrefix="piping"
                    options={PIPING_OPERATOR}
                    onChange={selectOperator}
                    value={selectedOperator}
                    isDisabled={disabled}
                />
                <div
                    className="row"
                    data-tip="custom show"
                    data-for="pipe-option-skip-helper"
                    data-delay-show="500"
                >
                    <Toggle
                        checked={enableSkip}
                        disabled={!selectedValue || disabled}
                        onChange={toggleEnableSkip}
                        label={'Enable skip'}
                    />
                    <ReactTooltip
                        className="tooltip"
                        id="pipe-option-skip-helper"
                        aria-haspopup="true"
                        effect="solid"
                        place="top"
                    >
                        <span>
                            Skip question if only one option is piped from
                            previous question. The option will be selected by
                            default instead.
                        </span>
                    </ReactTooltip>
                </div>
            </div>
            {!disabled && (
                <button
                    className="options--container--body--piping--close"
                    disabled={disabled}
                    onClick={onClose}
                >
                    <UilMultiply size={20} />
                </button>
            )}
        </div>
    );
};

const mapStateToProps = ({
    questionReducer,
    projectReducer
}: IApplicationState) => {
    return {
        questionSummaries:
            questionReducer.questionSummaries[
                projectReducer.currentProject.id
            ] || []
        // .filter(
        //     (q) =>
        //         PIPING_OPTION_QTYPES.includes(q.type as QTYPES) ||
        //         GROUP_QTYPES.includes(q.type as QTYPES)
        // )
    };
};

const mapDispatchToProps = (dispatch) => ({
    getQuestionById: (questionId: number, options, logic) =>
        dispatch(getQuestionByQuestionId(questionId, options))
});

export default connect(mapStateToProps, mapDispatchToProps)(Piping);
